import { AgentThreadMessage } from "@/store/chat.types";

const cloneMessage = (message: AgentThreadMessage): AgentThreadMessage => ({
  ...message,
  content: {
    ...message.content,
  },
});

const appendChunk = (
  target: AgentThreadMessage,
  chunk: AgentThreadMessage
): AgentThreadMessage => {
  const chunkIdx = chunk.content.chunk_idx ?? 0;
  const targetIdx = target.content.chunk_idx ?? -1;

  // For function messages with progress updates, always update progress fields
  const chunkProgress = chunk.content.progress ?? 0;
  const targetProgress = target.content.progress ?? 0;
  const hasProgressUpdate = chunkProgress > targetProgress;

  // Skip if neither chunk_idx nor progress has advanced
  if (chunkIdx <= targetIdx && !hasProgressUpdate) {
    return target;
  }

  return {
    ...target,
    content: {
      ...target.content,
      content: `${target.content.content}${chunk.content.content}`,
      chunk_idx: chunk.content.chunk_idx,
      // Update progress-related fields if present
      ...(chunk.content.progress !== undefined && {
        progress: chunk.content.progress,
      }),
      ...(chunk.content.total !== undefined && { total: chunk.content.total }),
    },
  };
};

export function mergeStreamingMessages(
  existingMessages: AgentThreadMessage[],
  incomingMessages: AgentThreadMessage[]
): AgentThreadMessage[] {
  const messageMap = new Map<string, AgentThreadMessage>();
  existingMessages.forEach((message) => {
    if (message.id) {
      messageMap.set(message.id, cloneMessage(message));
    }
  });

  const incomingMap = new Map<string, AgentThreadMessage[]>();
  incomingMessages.forEach((message) => {
    if (!message.id) {
      return;
    }
    if (!incomingMap.has(message.id)) {
      incomingMap.set(message.id, []);
    }
    incomingMap.get(message.id)!.push(message);
  });

  incomingMap.forEach((incomingList, id) => {
    const doneMsg = incomingList.find((message) => message.status === "done");
    if (doneMsg) {
      messageMap.set(id, cloneMessage(doneMsg));
      return;
    }

    const appendingMsgs = incomingList.filter(
      (message) => message.status === "appending"
    );
    if (appendingMsgs.length === 0) {
      return;
    }

    const existing = messageMap.get(id);
    if (existing && existing.status === "done") {
      return;
    }

    let nextMessage = existing
      ? cloneMessage(existing)
      : cloneMessage(appendingMsgs[0]);
    const chunksToMerge = existing ? appendingMsgs : appendingMsgs.slice(1);
    chunksToMerge.forEach((chunk) => {
      nextMessage = appendChunk(nextMessage, chunk);
    });
    messageMap.set(id, nextMessage);
  });

  return Array.from(messageMap.values());
}
